import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useParams, Outlet, useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import ReactFlow, {
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  Controls,
  NodeTypes,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Tooltip,
  useTheme,
  Grid,
} from '@mui/material';
import {
  LocationOn,
  ZoomIn,
  ZoomOut,
  Fullscreen,
  MyLocation,
  EvStation
} from '@mui/icons-material';
import NavigationBar from '../components/NavigationBar';
import GridNode from '../components/GridNode/GridNode';
import HandleTooltip from '../components/HandleTooltip';
import { siteConfigStorage } from '../utils/siteConfigStorage';
import { gpsService, projectService, siteConfigService, chargersService, organizationService } from '../services/api';
import { GPSData, ProjectInfo, SiteConfigNode, SiteConfigEdge, ProjectAsset, SiteConfigUnitData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { useOrgNavigation } from '../utils/navigation';
import { theme } from '../theme';
import { getRelativeTime } from '../utils/dateUtils';

// Battery SOC component - compact version with box shadows
const BatterySOC = ({ soc = 90 }: { soc?: number }) => {
  const theme = useTheme();
  
  // Determine SOC color based on percentage
  const getSOCColor = (soc: number) => {
    if (soc >= 30) return theme.palette.success.main;
    if (soc >= 15) return theme.palette.warning.light;
    if (soc >= 10) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Determine SOC text color based on percentage
  const getSOCTextColor = (soc: number) => {
    return soc > 55 ? 'white' : theme.palette.divider;
  };

  const socColor = getSOCColor(soc);
  const socHeight = Math.max(0, Math.min(100, soc)); // Clamp between 0-100

  return (
    <div style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Battery terminal - positioned at top */}
      <div style={{
        width: '6px',
        height: '2px',
        backgroundColor: socColor,
        borderRadius: '1px',
        marginBottom: '0px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }} />
      
      {/* Battery body */}
      <div style={{
        width: '32px',
        height: '44px',
        backgroundColor: 'white',
        borderRadius: '2px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }}>
        {/* SOC-based fill - height proportional to SOC percentage */}
        <div style={{
          position: 'absolute',
          left: '2px',
          right: '2px',
          bottom: '2px',
          height: `${socHeight * 0.4}px`, // 40px available height * percentage
          backgroundColor: socColor,
          borderRadius: '1px',
          transition: 'height 0.3s ease, background-color 0.3s ease'
        }} />
        
        {/* SOC percentage text - positioned at the SOC fill level */}
        <span style={{
          fontSize: '7px',
          fontWeight: 'bold',
          color: getSOCTextColor(soc),
          zIndex: 1,
          position: 'absolute',
          bottom: soc > 55 ? `${2 + (socHeight * 0.4) - 12}px` : `${2 + (socHeight * 0.4)}px`, // Below fill if >55%, above if ≤55%
          left: '50%',
          transform: 'translateX(-50%)',
        }}>
          {Math.round(soc)}%
        </span>
      </div>
    </div>
  );
};

// Mapbox access token - should be stored in environment variable
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Validate the access token format
const isValidMapboxToken = (token: string | undefined): boolean => {
  return !!(token && token.startsWith('pk.') && token.length > 50);
};

// Set access token only if valid
if (MAPBOX_ACCESS_TOKEN && isValidMapboxToken(MAPBOX_ACCESS_TOKEN)) {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
} else {
  console.warn('Invalid or missing Mapbox access token. Map functionality will be limited.');
}

// Custom node component for site config visualization
const CustomNode = ({ 
  data, 
  onTooltipShow, 
  onTooltipHide, 
  assetData,
  assets = [],
}: { 
  data: any;
  onTooltipShow?: (event: React.MouseEvent, handleType: 'CCS1' | 'CCS2' | 'AC' | 'INLET', assetId: string) => void;
  onTooltipHide?: () => void;
  assetData?: Record<string, SiteConfigUnitData>;
  assets?: ProjectAsset[];
}) => {
  const theme = useTheme();
  
  const label = data.label || '';
  const labelLower = label.toLowerCase();
  const assetType = data.assetType || assets.find((asset) => asset.unit_id === data.id)?.type;
  
  // Determine component type based on label prefix
  const isType1 = label.startsWith('Lader Tranby');
  const isType2 = label.startsWith('KE10') || label.startsWith('HB');
  const isType3 = label.startsWith('KE4') || label.startsWith('BP');
  const isType4 = label.startsWith('KE11') || label.startsWith('BC') || label.includes('BoostCharger');
  // Zaptec chargers are identified by ZPR prefix (Zaptec Pro) or containing "zaptec" or "pro" in the name
  const isZaptec = label.startsWith('ZPR') || labelLower.includes('zaptec') || (labelLower.includes('pro') && !isType1 && !isType2 && !isType3 && !isType4);
  // Easee chargers use the same single-AC layout as Zaptec. Serials start with EH (Easee Home) or EC (Easee Charge).
  const isEasee = labelLower.startsWith('eh') || labelLower.startsWith('ec') || labelLower.includes('easee');
  const isSingleAcCharger = isZaptec || isEasee;
  const isGrid = label === 'Grid';
  const isSwitchboard = assetType === 'switchboard';
  
  // Determine component dimensions
  const getComponentSize = () => {
    if (isSwitchboard) {
      return { width: '80px', height: '160px' };
    }
    if (isType2) {
      return { width: '80px', height: '60px' };
    }
    if (isType3) {
      return { width: '80px', height: '60px' }; // Make TYPE3 wider to fit labels inside
    }
    if (isType1 || isSingleAcCharger) {
      return { width: '80px', height: '60px' }; // Make TYPE1, Zaptec, and Easee wider to fit label inside
    }
    if (isType4) {
      return { width: '80px', height: '60px' }; // Make TYPE4 wider to fit all outlets
    }
    return { width: '40px', height: '40px' };
  };
  
  const componentSize = getComponentSize();

  // Get battery SOC from asset data for this unit
  const getBatterySOC = (): number => {
    if (!assetData || !data.id) {
      return 0; // Default to 0 if no data available
    }
    const unitData = assetData[data.id];
    if (!unitData || !unitData.battery || unitData.battery.length === 0) {
      return 0; // Default to 0 if no battery data
    }
    const batterySOC = unitData.battery[0]?.battery_soc;
    return batterySOC !== null && batterySOC !== undefined ? batterySOC : 0;
  };

  const batterySOC = getBatterySOC();

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
    }}>
      {/* Component label */}
      <div style={{
        textAlign: 'center',
        fontSize: '8px',
        lineHeight: '12px',
        color: theme.palette.divider,
        fontWeight: 500,
        maxWidth: '80px',
        wordWrap: 'break-word',
      }}>
        {label}
      </div>
      
      {/* Main component */}
      <div style={{
        display: 'inline-flex',
        width: componentSize.width,
        height: componentSize.height,
        padding: '8px',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        position: 'relative',
        backgroundColor: 'white',
      }}>
        {/* Input handle on the left */}
        <Handle
          type="target"
          position={Position.Left}
          style={{
            background: theme.palette.success.main,
            width: '8px',
            height: '8px',
            cursor: 'pointer',
            ...(isType2 && { top: '50%' }),
            ...(isType4 && { top: '50%' }),
            ...(isSingleAcCharger && { top: '50%' }),
            ...(isSwitchboard && { top: '50%' }),
          }}
          onMouseEnter={(event) => onTooltipShow?.(event, 'INLET', data.id)}
          onMouseLeave={onTooltipHide}
        />
        
        {/* Switchboard: up to 11 numbered outlets */}
        {isSwitchboard && (
          <>
            <img
              src={process.env.PUBLIC_URL + '/Switchboards.svg'}
              alt={label}
              style={{
                width: '24px',
                height: '24px',
              }}
            />
            {Array.from({ length: 11 }, (_, index) => (
              <React.Fragment key={`outlet-${index + 1}`}>
                <Handle
                  id={`outlet-${index + 1}`}
                  type="source"
                  position={Position.Right}
                  style={{
                    background: theme.palette.success.main,
                    width: '6px',
                    height: '6px',
                    top: `${(index + 1) * 8}%`,
                    cursor: 'pointer',
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: `${(index + 1) * 8}%`,
                  transform: 'translateY(-50%)',
                  fontSize: '8px',
                  color: '#000000',
                  fontWeight: 400,
                }}>
                  {index + 1}
                </div>
              </React.Fragment>
            ))}
          </>
        )}

        {/* TYPE2 and TYPE4: Show battery indicator */}
        {(isType2 || isType4) && (
          <div style={{ 
            position: 'absolute',
            left: '37.5%',
            top: '45%',
            transform: 'translate(-50%, -50%)'
          }}>
            <BatterySOC soc={batterySOC} />
          </div>
        )}
        
        {/* TYPE1, TYPE3, Zaptec, and Easee: Show icon if needed */}
        {(isType1 || isType3 || isSingleAcCharger) && (
          <img
            src={process.env.PUBLIC_URL + '/Chargers.svg'}
            alt={label}
            style={{
              width: '24px',
              height: '24px',
              ...((isType1 || isType3 || isSingleAcCharger) && { position: 'absolute', left: '14px' }) // Position icon on left for TYPE1, TYPE3, Zaptec, and Easee to make room for labels
            }}
          />
        )}
        
        {/* TYPE1: One AC outlet with label inside */}
        {isType1 && (
          <>
            {/* Vertical line dividing the box between icon and label */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'AC', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '2px', // Inside the box, near the handle
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
          </>
        )}
        
        {/* Zaptec and Easee: One AC outlet with label inside (similar to TYPE1) */}
        {isSingleAcCharger && (
          <>
            {/* Vertical line dividing the box between icon and label */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'AC', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '2px', // Inside the box, near the handle
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
          </>
        )}
        
        {/* TYPE2: AC outlet and CCS1 outlet, with battery */}
        {isType2 && (
          <>
            {/* Vertical line dividing the battery box */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            {/* AC outlet on right */}
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '25%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'AC', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '2px',
              top: '25%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
            
            {/* CCS1 outlet on right */}
            <Handle
              id="CCS1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '75%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'CCS1', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '2px',
              top: '75%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
          </>
        )}
        
        {/* TYPE3: CCS1 and CCS2 outlets with labels inside */}
        {isType3 && (
          <>
            {/* Vertical line dividing the box between icon and labels */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            <Handle
              id="ccs1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '25%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'CCS1', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '4px', // Inside the box, near the handle
              top: '25%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
            <Handle
              id="ccs2"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '75%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'CCS2', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '4px', // Inside the box, near the handle
              top: '75%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS2
            </div>
          </>
        )}
        
        {/* TYPE4: AC, CCS1, and CCS2 outlets with battery */}
        {isType4 && (
          <>
            {/* Vertical line dividing the battery box */}
            <div style={{
              position: 'absolute',
              right: '28px',
              top: '0px',
              bottom: '0px',
              width: '1px',
              backgroundColor: '#CDC9C5',
            }} />
            
            {/* AC outlet on right (top) */}
            <Handle
              id="ac-outlet"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '20%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'AC', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '20%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              AC
            </div>
            
            {/* CCS1 outlet on right (middle) */}
            <Handle
              id="CCS1"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '50%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'CCS1', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS1
            </div>
            
            {/* CCS2 outlet on right (bottom) */}
            <Handle
              id="CCS2"
              type="source"
              position={Position.Right}
              style={{
                background: theme.palette.success.main,
                width: '6px',
                height: '6px',
                top: '80%',
                cursor: 'pointer',
              }}
              onMouseEnter={(event) => onTooltipShow?.(event, 'CCS2', data.id)}
              onMouseLeave={onTooltipHide}
            />
            <div style={{
              position: 'absolute',
              right: '4px',
              top: '80%',
              transform: 'translateY(-50%)',
              fontSize: '8px',
              color: '#000000',
              fontWeight: 400,
            }}>
              CCS2
            </div>
          </>
        )}
        
        {/* Other: Only one inlet (no output handles) */}
        {!isType1 && !isType2 && !isType3 && !isType4 && !isSingleAcCharger && !isGrid && !isSwitchboard && (
          // No output handles for other types
          null
        )}
      </div>
    </div>
  );
};

// Node types for React Flow - we'll create this dynamically to pass props
const createNodeTypes = (
  onTooltipShow: (event: React.MouseEvent, handleType: 'CCS1' | 'CCS2' | 'AC' | 'INLET', assetId: string) => void,
  onTooltipHide: () => void,
  assetData: Record<string, SiteConfigUnitData>,
  assets: ProjectAsset[],
): NodeTypes => {
  return {
    custom: (props: any) => (
      <CustomNode 
        {...props} 
        onTooltipShow={onTooltipShow}
        onTooltipHide={onTooltipHide}
        assetData={assetData}
        assets={assets}
      />
    ),
    grid: GridNode,
  };
};

// Project interface matching the one used in Projects component
interface Project {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  address: string;
  units: string;
  createdAt: Date;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

// Custom marker component matching ChargerDetails.tsx
const EvStationMarker: React.FC = () => {
  return (
    <div
      style={{
        width: '40px',
        height: '40px',
        backgroundColor: theme.palette.secondary.dark,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        cursor: 'pointer',
      }}
    >
      <EvStation style={{ fontSize: '24px' }} />
    </div>
  );
};

const ProjectDetails: React.FC = () => {
  const theme = useTheme();
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const { setProjectBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { navigateToOrgPath } = useOrgNavigation();
  const [project, setProject] = useState<Project | null>(null);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Site config state
  const [siteConfigLoading, setSiteConfigLoading] = useState(false);
  const [siteConfigError, setSiteConfigError] = useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  
  // Asset data state for tooltips
  const [assets, setAssets] = useState<ProjectAsset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetData, setAssetData] = useState<Record<string, SiteConfigUnitData>>({});
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    handleType: 'CCS1' | 'CCS2' | 'AC' | 'INLET';
    assetId: string;
  } | null>(null);
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Map-related state
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markers = useRef<mapboxgl.Marker[]>([]);
  const [mapError, setMapError] = useState<string | null>(null);
  const [hasValidToken] = useState(isValidMapboxToken(MAPBOX_ACCESS_TOKEN));
  const [mapLoaded, setMapLoaded] = useState(false);
  const [gpsData, setGpsData] = useState<GPSData[]>([]);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [mapInitializationTimeout, setMapInitializationTimeout] = useState(false);
  const isFetchingGPS = useRef(false); // Track if GPS fetch is in progress
  const fetchedUnitIds = useRef<Set<string>>(new Set()); // Track which unit_ids have been fetched

  // Map controls configuration - memoized to prevent re-renders
  const mapControls = useMemo(() => [
    { icon: <ZoomIn />, action: 'zoomIn' },
    { icon: <ZoomOut />, action: 'zoomOut' },
    { icon: <MyLocation />, action: 'location' },
    { icon: <Fullscreen />, action: 'fullscreen' },
  ], []);

  // Function to fetch site config data
  const fetchSiteConfigData = useCallback(async () => {
    if (!projectId || !user?.orgId) {
      return;
    }

    setSiteConfigLoading(true);
    setSiteConfigError(null);

    try {
        const response = await siteConfigService.getSiteConfigProject(projectId, user.orgId);
        
        if (response.success && response.data) {
          const graph = (response.data as any).graph;
          const hasGraph = graph && Array.isArray(graph.nodes) && Array.isArray(graph.edges);

          if (!hasGraph || graph.nodes.length === 0) {
            // No site configuration present
            setNodes([]);
            setEdges([]);
          } else {
            
            // Convert API response to React Flow format
            const flowNodes: Node[] = graph.nodes.map((node: SiteConfigNode) => {
              return {
                id: node.id,
                type: node.data.label === 'Grid' ? 'grid' : 'custom',
                position: node.position,
                data: {
                  id: node.id,
                  label: node.data.label,
                  ...(node.data.assetType && { assetType: node.data.assetType }),
                },
              };
            });

            const flowEdges: Edge[] = graph.edges.map((edge: SiteConfigEdge) => {
              return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: 'default',
                ...(edge.sourceHandle && { sourceHandle: edge.sourceHandle }),
              };
            });

            setNodes(flowNodes);
            // Small delay to ensure nodes are rendered before edges
            setTimeout(() => {
              setEdges(flowEdges);
            }, 100);
          }
      } else {
        setSiteConfigError(response.message || 'Failed to fetch site config data');
      }
    } catch (error) {
      setSiteConfigError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setSiteConfigLoading(false);
    }
  }, [projectId, user?.orgId, setNodes, setEdges]);

  // Function to fetch project assets
  const fetchAssets = async () => {
    if (!projectId || !user?.orgId) {
      return;
    }

    setAssetsLoading(true);

    try {
      const response = await chargersService.getProjectAssets(projectId, user.orgId);
      
      if (response.success && response.data) {
        const projectAssets = response.data.assets || [];
        setAssets(projectAssets);
        setAssetsLoading(false);

        if (projectAssets.length === 0) {
          setLoadingGPS(false);
          setGpsData([]);
          setGpsError(null);
        }

        void fetchAssetDataForAssets(projectAssets);
      } else {
        setAssets([]);
        setAssetsLoading(false);
        setLoadingGPS(false);
        setGpsData([]);
      }
    } catch (error) {
      setAssets([]);
      setAssetsLoading(false);
      setLoadingGPS(false);
      setGpsData([]);
    }
  };

  // Paint site-config details as each unit returns — do not block the asset list
  const fetchAssetDataForAssets = async (projectAssets: ProjectAsset[]) => {
    if (!user?.orgId) return;

    projectAssets.forEach(async (asset) => {
      try {
        const response = await siteConfigService.getSiteConfigUnit(asset.unit_id, user.orgId!);
        if (response.success && response.data) {
          setAssetData((prev) => ({
            ...prev,
            [asset.unit_id]: response.data,
          }));
        }
      } catch {
        // Site config is optional; the asset is already visible
      }
    });
  };

  const isValidGpsPoint = useCallback((point: GPSData) => {
    if (point.latitude == null || point.longitude == null) return false;
    if (point.latitude === 0 || point.longitude === 0) return false;
    if (point.latitude < -90 || point.latitude > 90) return false;
    if (point.longitude < -180 || point.longitude > 180) return false;
    return true;
  }, []);

  const mergeGpsPoints = useCallback((prev: GPSData[], incoming: GPSData[]) => {
    const existing = new Set(prev.map((d) => `${d.unit_id}-${d.timestamp}`));
    const unique = incoming.filter(
      (d) => isValidGpsPoint(d) && !existing.has(`${d.unit_id}-${d.timestamp}`)
    );
    if (unique.length === 0) return prev;

    const merged = [...prev, ...unique];
    if (prev.length === 0 && map.current?.loaded()) {
      const latestLocation = [...merged].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )[0];
      map.current.setCenter([latestLocation.longitude, latestLocation.latitude]);
      map.current.setZoom(16);
    }
    return merged;
  }, [isValidGpsPoint]);

  // Function to fetch GPS data for all assets
  const fetchGPSData = useCallback(async () => {
    if (!assets || assets.length === 0) {
      setGpsData([]);
      setGpsError(null);
      setLoadingGPS(false);
      isFetchingGPS.current = false;
      return;
    }

    if (!user?.orgId) {
      setLoadingGPS(false);
      isFetchingGPS.current = false;
      return;
    }

    if (isFetchingGPS.current) {
      return;
    }

    const assetsToFetch = assets.filter((asset) => !fetchedUnitIds.current.has(asset.unit_id));
    if (assetsToFetch.length === 0) {
      setLoadingGPS(false);
      return;
    }

    isFetchingGPS.current = true;
    const orgId = user.orgId;
    setLoadingGPS(true);
    setGpsError(null);

    const nameByUnit = new Map(assets.map((asset) => [asset.unit_id, asset.unit_name]));
    const projectUnits = new Set(assets.map((asset) => asset.unit_id));

    try {
      const response = await organizationService.getOrganizationGPS(orgId, projectId);
      if (response.success && response.data) {
        const points = [
          ...(response.data.chargers || []),
          ...(response.data.machine || []),
        ]
          .filter((point) => !point.unit_id || projectUnits.has(point.unit_id))
          .map((point) => ({
            ...point,
            unit_name: point.unit_name || nameByUnit.get(point.unit_id || '') || point.unit_id,
          }));

        assets.forEach((asset) => fetchedUnitIds.current.add(asset.unit_id));
        setGpsData((prev) => mergeGpsPoints(prev, points));
        return;
      }

      throw new Error(response.message || 'Organization GPS failed');
    } catch {
      await Promise.allSettled(
        assetsToFetch.map(async (asset) => {
          try {
            const response = await gpsService.getUnitGPS(asset.unit_id, orgId);
            fetchedUnitIds.current.add(asset.unit_id);
            const history = response.success ? response.data?.data : undefined;
            if (!history || history.length === 0) return;

            const latest = history[history.length - 1];
            const point = {
              ...latest,
              unit_id: asset.unit_id,
              unit_name: asset.unit_name,
            };
            setGpsData((prev) => mergeGpsPoints(prev, [point]));
            setLoadingGPS(false);
          } catch {
            fetchedUnitIds.current.add(asset.unit_id);
          }
        })
      );
    } finally {
      setLoadingGPS(false);
      isFetchingGPS.current = false;
    }
  }, [assets, user?.orgId, projectId, mergeGpsPoints]);


  // Function to handle navigation to charger detail
  const handleChargerNavigation = useCallback((unitId: string) => {
    const chargerPath = `/assets/chargers/${unitId}`;
    const orgPath = navigateToOrgPath(chargerPath);
    navigate(orgPath);
  }, [navigate, navigateToOrgPath]);

  // Function to add markers to the map
  const addMarkersToMap = useCallback(() => {
    if (!map.current || !gpsData || gpsData.length === 0) {
      return;
    }

    // Set up global handler for charger navigation
    (window as any).handleChargerClick = handleChargerNavigation;

    // Clear existing markers
    markers.current.forEach(marker => marker.remove());
    markers.current = [];

    // Add new markers
    gpsData.forEach((location) => {
      // Create custom EvStation icon element using React
      const el = document.createElement('div');
      const root = createRoot(el);
      root.render(<EvStationMarker />);
      
      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.longitude, location.latitude])
        .addTo(map.current!);

      // Add popup with location info (matching Map.tsx design)
      const unitName = location.unit_name || 'Unknown Unit';
      const unitId = location.unit_id;
      const isCharger = !!unitId; // Assume all units with unit_id are chargers
      
      const popup = new mapboxgl.Popup({
        offset: 25,
        closeButton: false,
        closeOnClick: true,
        maxWidth: '320px' // Prevent overflow while allowing a bit more width
      }).setHTML(`
        <div style="
          box-sizing: border-box;
          min-width: 240px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <!-- Top Section: Asset Name and GO TO button -->
          <div style="
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0;
            width: 100%;
          ">
            <div style="display: flex; align-items: center; gap: 8px;">
              <img 
                src="${process.env.PUBLIC_URL}/ev_station.svg" 
                alt="EV Station" 
                style="
                  width: 32px;
                  height: 32px;
                  flex-shrink: 0;
                  filter: brightness(0) saturate(100%) invert(42%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
                "
              />
              <span style="font-size: 16px; font-weight: 600; color: #666666;">
                ${unitName}
              </span>
            </div>
      
            ${isCharger && unitId ? `
               <div
                 style="
                   display: flex;
                   align-items: center;
                   gap: 4px;
                   color: #4F785C;
                   cursor: pointer;
                   font-size: 14px;
                   font-weight: 400;
                   flex-shrink: 0;
                   margin: 4px;
                   padding: 4px 4px;
                   border-radius: 4px;
                   transition: background-color 0.2s ease;
                 "
                 onmouseover="this.style.backgroundColor='#4F785C20'"
                 onmouseout="this.style.backgroundColor='transparent'"
                 onclick="window.handleChargerClick('${unitId}')"
               >
                <span>GO TO</span>
                <img
                  src="${process.env.PUBLIC_URL}/arrow_forward.svg"
                  alt="Go to"
                  style="
                    width: 20px;
                    height: 20px;
                    filter: brightness(0) saturate(100%) invert(27%) sepia(15%) saturate(1200%) hue-rotate(90deg) brightness(90%) contrast(85%);
                  "
                />
              </div>
            ` : ''}
          </div>
          
          <!-- Divider -->
          <div style="
            width: 100%;
            height: 1px;
            background-color: #B5AFA9;
            margin: 12px 0;
          "></div>
          
          <!-- Bottom Section: GPS Location (Lat/Lng) -->
          <div style="
            display: flex;
            flex-direction: row;
            gap: 4px;
            font-size: 12px;
            font-family: 'Montserrat', sans-serif;
            color: rgba(0, 0, 0, 0.87);
            margin-bottom: 8px;
          ">
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lat:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.latitude.toFixed(6)}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 4px;">
              <span style="font-weight: 600; color: rgba(0, 0, 0, 0.6);">Lng:</span>
              <span style="color: rgba(0, 0, 0, 0.87);">${location.longitude.toFixed(6)}</span>
            </div>
          </div>
          
          <!-- GPS Timestamp -->
          <div style="
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background-color: #0035A2;
            color: white;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-family: 'Montserrat', sans-serif;
            height: 24px;
            line-height: 16px;
            letter-spacing: 0.16px;
            margin-top: 0px;
          ">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="currentColor"
              style="color: white;"
            >
              <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"/>
            </svg>
            <span style="color: white; font-size: 12px; line-height: 16px; letter-spacing: 0.16px;">
              ${getRelativeTime(location.timestamp)} (GPS)
            </span>
          </div>
        </div>
      `);

      marker.setPopup(popup);
      markers.current.push(marker);
    });
  }, [gpsData, handleChargerNavigation]);

  const handleMapControl = useCallback((action: string) => {
    if (!map.current) return;

    switch (action) {
      case 'zoomIn':
        map.current.zoomIn();
        break;
      case 'zoomOut':
        map.current.zoomOut();
        break;
      case 'location':
        // Get user's current location
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { longitude, latitude } = position.coords;
              map.current!.flyTo({
                center: [longitude, latitude],
                zoom: 14
              });
            },
            () => {
              // Silently handle location error
            }
          );
        }
        break;
      case 'fullscreen':
        // Toggle fullscreen
        if (mapContainer.current) {
          if (document.fullscreenElement) {
            document.exitFullscreen();
          } else {
            mapContainer.current.requestFullscreen();
          }
        }
        break;
    }
  }, []);

  const handleMapRetry = useCallback(() => {
    setMapError(null);
    setMapInitializationTimeout(false);
    setMapLoaded(false);
    // Force re-initialization by clearing the map reference
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    // Trigger re-initialization
    setTimeout(() => {
      const container = mapContainer.current;
      if (container && hasValidToken) {
        try {
          // Determine initial center and zoom based on GPS data availability
          let initialCenter: [number, number] = [0, 0];
          let initialZoom = 2;
          
          if (gpsData && gpsData.length > 0) {
            const sortedData = [...gpsData].sort((a, b) => 
              new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
            );
            const latestLocation = sortedData[0];
            initialCenter = [latestLocation.longitude, latestLocation.latitude];
            initialZoom = 12;
          }

          map.current = new mapboxgl.Map({
            container: container,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: initialCenter,
            zoom: initialZoom
          });
          map.current.on('load', () => {
            setMapError(null);
            setMapInitializationTimeout(false);
            setMapLoaded(true);
            
            // Add markers if GPS data is available
            if (map.current && gpsData && gpsData.length > 0) {
              addMarkersToMap();
              
              // Center map on the latest GPS location (without animation)
              const sortedData = [...gpsData].sort((a, b) => 
                new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
              );
              const latestLocation = sortedData[0];
              map.current.setCenter([latestLocation.longitude, latestLocation.latitude]);
              map.current.setZoom(16);
            }
          });
        } catch (error) {
          setMapError('Map retry failed');
        }
      }
    }, 100);
  }, [hasValidToken, gpsData, addMarkersToMap]);

  // Handle node click for navigation to ChargerDetails
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    const label = node.data.label || '';
    
    // Check if the clicked node is TYPE2 or TYPE4 (both have batteries and should be clickable)
    const isType2 = label.startsWith('KE1') || label.startsWith('HB');
    const isType4 = label.includes('Thor V3') || label.includes('BoostCharger');
    
    if (isType2 || isType4) {
      // Navigate to ChargerDetails page using the node id as unit_id
      const chargerPath = `/assets/chargers/${node.id}`;
      const orgPath = navigateToOrgPath(chargerPath);
      navigate(orgPath);
    }
  }, [navigate, navigateToOrgPath]);

  // Handle tooltip events
  const handleTooltipShow = useCallback((event: React.MouseEvent, handleType: 'CCS1' | 'CCS2' | 'AC' | 'INLET', assetId: string) => {
    // Clear any existing timeout
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
    
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    
    // Use viewport coordinates directly for fixed positioning
    const tooltipData = {
      visible: true,
      x: rect.right + 10, // Position to the right of the handle
      y: rect.top + rect.height / 2, // Center vertically with the handle
      handleType,
      assetId
    };
    setTooltipData(tooltipData);
  }, [tooltipTimeout]);

  const handleTooltipHide = useCallback(() => {
    // Clear timeout when manually hiding
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
    
    setTooltipData(null);
  }, [tooltipTimeout]);

  // Helper function to get tooltip data for a specific handle
  const getTooltipData = useCallback((assetId: string, handleType: 'CCS1' | 'CCS2' | 'AC' | 'INLET') => {
    const asset = assetData[assetId];
    if (!asset) {
      return { power: null, voltage: null, current: null, connected: false };
    }
    
    // Find the node to get the label
    const node = nodes.find(n => n.id === assetId);
    const label = node?.data?.label || '';
    const shouldDividePower = label.startsWith('KE');
    
    // Helper function to divide power by 1000 if needed
    const adjustPower = (power: number | null): number | null => {
      if (power === null || power === undefined) return null;
      return shouldDividePower ? power / 1000 : power;
    };
    
    if (handleType === 'AC') {
      const acData = asset.ac;
      if (!acData || acData.length === 0) {
        return { power: null, voltage: null, current: null, connected: false };
      }
      
      const data = acData[0];
      return {
        power: adjustPower(data.ac_power as number | null),
        voltage: data.ac_voltage as number | null,
        current: null, // AC doesn't have current data
        connected: false, // AC doesn't have connection status
      };
    }
    
    if (handleType === 'INLET') {
      const gridData = asset.grid_input;
      if (!gridData || gridData.length === 0) {
        return { power: null, voltage: null, current: null, connected: false };
      }
      
      const data = gridData[0];
      return {
        power: adjustPower(data.input_power as number | null),
        voltage: data.input_voltage as number | null,
        current: null, // Grid input doesn't have current data
        connected: false, // Grid input doesn't have connection status
      };
    }
    
    // Handle CCS1 and CCS2
    if (handleType === 'CCS1') {
      const ccs1Data = asset.ccs1;
      if (!ccs1Data || ccs1Data.length === 0) {
        return { power: null, voltage: null, current: null, connected: false };
      }
      
      const data = ccs1Data[0];
      return {
        power: adjustPower(data.ccs1_power as number | null),
        voltage: data.ccs1_voltage as number | null,
        current: data.ccs1_current as number | null,
        connected: data.ccs1_connected as boolean,
      };
    }
    
    if (handleType === 'CCS2') {
      const ccs2Data = asset.ccs2;
      if (!ccs2Data || ccs2Data.length === 0) {
        return { power: null, voltage: null, current: null, connected: false };
      }
      
      const data = ccs2Data[0];
      return {
        power: adjustPower(data.ccs2_power as number | null),
        voltage: data.ccs2_voltage as number | null,
        current: data.ccs2_current as number | null,
        connected: data.ccs2_connected as boolean,
      };
    }
    
    // Fallback (should not reach here)
    return { power: null, voltage: null, current: null, connected: false };
  }, [assetData, nodes]);

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };


  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId || !user?.orgId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      
      try {
        // Fetch project info from API
        const response = await projectService.getProjectInfo(projectId, user.orgId);
        
        if (response.success && response.data) {
          const projectData = response.data;
          
          // Create project object from API response
          const projectFromApi: Project = {
            id: projectData.project_id,
            name: projectData.project_name,
            startDate: projectData.start_time || '',
            endDate: projectData.end_time || '',
            address: projectData.address || '',
            units: '', // No longer needed
            createdAt: new Date(), // No longer needed
          };
          
          setProject(projectFromApi);
          setProjectInfo(projectData);
        } else {
          // Fallback to mock data if API fails
          const mockProject: Project = {
            id: projectId,
            name: 'Project Not Found',
            startDate: '',
            endDate: '',
            address: '',
            units: '',
            createdAt: new Date(),
          };
          setProject(mockProject);
        }
      } catch (error) {
        // Fallback to mock data if API fails
        const mockProject: Project = {
          id: projectId,
          name: 'Project Not Found',
          startDate: '',
          endDate: '',
          address: '',
          units: '',
          createdAt: new Date(),
        };
        setProject(mockProject);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId, user?.orgId]);

  // Set breadcrumbs when project data is loaded
  useEffect(() => {
    if (project && project.name) {
      setProjectBreadcrumbs(project.name);
    }
  }, [project, setProjectBreadcrumbs]);


  // Listen for configuration changes and refresh the viewer
  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = siteConfigStorage.subscribe((changedProjectId: string) => {
      if (changedProjectId === projectId) {
        // Trigger a refetch of site config when configuration changes
        fetchSiteConfigData();
      }
    });

    return unsubscribe;
  }, [projectId, fetchSiteConfigData]);

  // Ensure loadingGPS is false when there are no assets
  useEffect(() => {
    if (!assetsLoading && assets.length === 0) {
      setLoadingGPS(false);
    }
  }, [assets.length, assetsLoading]);

  // Create a stable string representation of unit_ids to use as dependency
  const assetUnitIdsString = useMemo(() => {
    return assets.map(a => a.unit_id).sort().join(',');
  }, [assets]);

  // Fetch GPS data when assets are loaded
  useEffect(() => {
    // Only fetch GPS data if assets are loaded (not loading) and we have assets
    // If there are no assets, ensure loadingGPS is false
    if (!assetsLoading) {
      if (assets.length === 0) {
        // No assets - ensure GPS loading state is cleared immediately
        setLoadingGPS(false);
        setGpsData([]);
        setGpsError(null);
        fetchedUnitIds.current.clear(); // Clear fetched unit_ids when no assets
      } else {
        // We have assets - fetch GPS data (only for unit_ids not yet fetched)
        fetchGPSData();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetUnitIdsString, assetsLoading, user?.orgId, fetchGPSData]);

  // Fetch site config data on component mount
  useEffect(() => {
    fetchSiteConfigData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.orgId]);

  // Fetch assets data on component mount
  useEffect(() => {
    fetchAssets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.orgId]);

  // Cleanup tooltip timeout on unmount
  useEffect(() => {
    return () => {
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
      }
    };
  }, [tooltipTimeout]);

  // Memoize nodeTypes to prevent React Flow warnings
  const nodeTypes = useMemo(() => createNodeTypes(handleTooltipShow, handleTooltipHide, assetData, assets), [handleTooltipShow, handleTooltipHide, assetData, assets]);

  // Reset fetched unit_ids when the actual unit_ids change
  useEffect(() => {
    const currentUnitIds = new Set(assets.map(a => a.unit_id));
    // Remove unit_ids that are no longer in the assets list
    fetchedUnitIds.current.forEach(unitId => {
      if (!currentUnitIds.has(unitId)) {
        fetchedUnitIds.current.delete(unitId);
      }
    });
  }, [assets, assetUnitIdsString]);

  // Refetch GPS data if we don't have any data and we have assets
  useEffect(() => {
    if (gpsData.length === 0 && !loadingGPS && !gpsError && assets.length > 0) {
      fetchGPSData();
    }
  }, [gpsData.length, loadingGPS, gpsError, assets.length, assetUnitIdsString, fetchGPSData]);

  // Initialize map when component mounts and project is loaded (only once)
  useEffect(() => {
    // Only initialize map when all conditions are met
    if (map.current || !mapContainer.current || !hasValidToken || typeof mapboxgl === 'undefined') {
      return;
    }

    // Function to initialize the map
    const initializeMap = () => {
      try {
        const container = mapContainer.current!;
        
        // Ensure container has dimensions
        if (container.offsetWidth === 0 || container.offsetHeight === 0) {
          setTimeout(initializeMap, 500);
          return;
        }

        // Force container to have minimum dimensions if needed
        if (container.offsetHeight < 200) {
          container.style.height = '200px';
        }

        // Start with world view - GPS data will update center/zoom later
        const initialCenter: [number, number] = [0, 0];
        const initialZoom = 2;

        map.current = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v11',
          center: initialCenter,
          zoom: initialZoom
        });

        map.current.on('load', () => {
          setMapError(null);
          setMapInitializationTimeout(false);
          setMapLoaded(true);
          
          // Trigger a resize to ensure proper rendering in the card layout
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
            }
          }, 100);
        });

        map.current.on('error', (e) => {
          if (e.error && e.error.message) {
            setMapError(`Map error: ${e.error.message}`);
          } else {
            setMapError('An error occurred while loading the map');
          }
        });
      } catch (error) {
        setMapError(`Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    // Start initialization with a delay to ensure DOM is ready
    const timer = setTimeout(initializeMap, 1000);
    
    // Set a timeout to prevent infinite loading
    const timeoutTimer = setTimeout(() => {
      if (!map.current) {
        setMapInitializationTimeout(true);
        setMapError('Map initialization timed out. Please refresh the page.');
      }
    }, 15000);

    return () => {
      clearTimeout(timer);
      clearTimeout(timeoutTimer);
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      setMapLoaded(false); // Reset map loaded state on cleanup
      // Clean up global function
      delete (window as any).handleChargerClick;
    };
  }, [hasValidToken]);

  // Effect to add markers and center map when GPS data changes
  useEffect(() => {
    if (map.current && map.current.loaded() && gpsData && gpsData.length > 0) {
      addMarkersToMap();
      
      // Center map on the latest GPS location (without animation)
      const sortedData = [...gpsData].sort((a, b) => 
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      const latestLocation = sortedData[0];
      
      map.current.setCenter([latestLocation.longitude, latestLocation.latitude]);
      map.current.setZoom(16);
    }
  }, [addMarkersToMap, gpsData, mapLoaded]); // Use mapLoaded instead of map ref

  // Effect to resize map when component becomes visible
  useEffect(() => {
    if (map.current) {
      // Small delay to ensure the container is fully rendered
      const timer = setTimeout(() => {
        if (map.current) {
          map.current.resize();
        }
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [loading, project]);

  if (!loading && !project) {
    return (
      <Box sx={{ 
        flexGrow: 1, 
        backgroundColor: '#FAFAFA', 
        p: 3,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 200px)'
      }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
          Project not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 0 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: 'auto', // Auto height to fit content
        minHeight: 'fit-content'
      }}>
        {/* Top Container - Project Information */}
        <Box sx={{ 
          height: '79px',
          // backgroundColor: 'white',
          // borderRadius: '8px',
          // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          px: 3, // 24px padding left and right
          overflow: 'auto'
        }}>
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: '48px',
            alignItems: 'flex-start',
            width: '100%',
            flexWrap: 'wrap'
          }}>
            {/* Section 1: Project Name with Ongoing Status */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '200px' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
                {project?.name || 'Loading project...'}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                backgroundColor: theme.palette.secondary.dark,
                color: 'white',
                px: 1.5,
                py: 0.5,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 500
              }}>
                Ongoing
              </Box>
            </Box>

            {/* Section 2: Start Time and End Time with Icons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '150px' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1, fontSize: '0.875rem' }}>
                {project?.startDate ? formatDate(project.startDate) : 'TBD'} - {project?.endDate ? formatDate(project.endDate) : 'TBD'}
              </Typography>
              
              {/* Metrics Row */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                gap: 2,
                flexWrap: 'wrap'
              }}>
                <Tooltip title="Number of chargers" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <img src={process.env.PUBLIC_URL + '/Chargers.svg'} alt="Chargers" style={{ width: '16px', height: '16px', filter: 'brightness(0)' }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>{projectInfo?.chargers || 0}</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Number of energy meters" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <img src={process.env.PUBLIC_URL + '/Energy Meters.svg'} alt="Energy Meters" style={{ width: '16px', height: '16px', filter: 'brightness(0)' }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>{projectInfo?.energy_meter || 0}</Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="Number of excavators" arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <img src={process.env.PUBLIC_URL + '/DiggerIcon.svg'} alt="Excavators" style={{ width: '16px', height: '16px', filter: 'brightness(0)' }} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>{projectInfo?.excavators || 0}</Typography>
                  </Box>
                </Tooltip>
              </Box>
            </Box>

            {/* Section 3: City, Country with TBD */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: '150px' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                {project?.address || 'TBD'}
              </Typography>
              
              {/* TBD Text */}
              {/* <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                TBD
              </Typography> */}
            </Box>
          </Box>
        </Box>

        {/* New Container with 2 horizontally spaced sub-containers */}
        <Grid container spacing={3} sx={{ p: 3 }}>
          {/* First Sub-container - Enlinjeskjema */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              height: 397,
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>

              {/* Site Configuration React Flow */}
              <Box 
                data-testid="site-config-container"
                sx={{ 
                  height: 'calc(100% - 0px)', 
                  position: 'relative',
                  backgroundColor: theme.palette.background.default,
                  borderRadius: '4px',
                }}>
                {siteConfigLoading && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                      Loading site configuration...
                    </Typography>
                  </Box>
                )}
                
                {siteConfigError && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    textAlign: 'center',
                    p: 2
                  }}>
                    <Alert severity="error" sx={{ fontSize: '12px' }}>
                      {siteConfigError}
                    </Alert>
                  </Box>
                )}
                
                {!siteConfigLoading && !siteConfigError && nodes.length === 0 && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: '50%', 
                    left: '50%', 
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    textAlign: 'center',
                    p: 2
                  }}>
                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                      This site has not been configured.
                      Do it in "Site Config" below.
                    </Typography>
                  </Box>
                )}
                
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onNodeClick={handleNodeClick}
                  nodeTypes={nodeTypes}
                  fitView
                  zoomOnScroll={false}
                  attributionPosition="bottom-left"
                >
                  <Controls 
                    showZoom={false}
                    showInteractive={false}
                    showFitView={false}
                  />
                </ReactFlow>
                
                {/* Tooltip overlay */}
                {tooltipData && tooltipData.visible && (() => {
                  const tooltipInfo = getTooltipData(tooltipData.assetId, tooltipData.handleType);
                  
                  return (
                    <Box
                      sx={{
                        position: 'fixed',
                        left: tooltipData.x,
                        top: tooltipData.y,
                        transform: 'translateY(-50%)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                      }}
                    >
                      <HandleTooltip
                        handleType={tooltipData.handleType}
                        power={tooltipInfo.power}
                        voltage={tooltipInfo.voltage}
                        current={tooltipInfo.current}
                        connected={tooltipInfo.connected}
                      />
                    </Box>
                  );
                })()}
                
              </Box>
            </Box>
          </Grid>

          {/* Second Sub-container - Map View */}
          <Grid item xs={12} md={6}>
          <Paper sx={{ 
            height: 397,
            borderRadius: 1,
            boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
            overflow: 'hidden',
            position: 'relative',
          }}>
            {/* Map container */}
            <Box sx={{ 
              //height: 320,
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div 
                ref={mapContainer} 
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '320px',
                  position: 'relative',
                  backgroundColor: '#f5f5f5',
                }} 
              />
              
              {/* Loading indicator for map - only show while map is actually loading */}
              {!mapLoaded && !mapError && !mapInitializationTimeout && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}

              {/* Fallback map placeholder if initialization fails */}
              {!map.current && (mapError || mapInitializationTimeout) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    zIndex: 5,
                    textAlign: 'center',
                    padding: 2,
                  }}
                >
                  <LocationOn sx={{ fontSize: 48, color: 'rgba(0,0,0,0.3)' }} />
                  <Typography variant="body2" sx={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>
                    Map unavailable
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.4)' }}>
                    {mapError || 'Initialization failed'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(0,0,0,0.4)' }}>
                    GPS data will still be shown below
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleMapRetry}
                    sx={{ mt: 1, fontSize: '12px' }}
                  >
                    Retry Map
                  </Button>
                </Box>
              )}

              {/* Error Display */}
              {mapError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity={hasValidToken ? "error" : "warning"}
                    sx={{ fontSize: '12px' }}
                  >
                    {mapError}
                  </Alert>
                </Box>
              )}

              {/* GPS Error Display */}
              {gpsError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="error"
                    sx={{ fontSize: '12px' }}
                  >
                    GPS Position Unavailable: {gpsError}
                  </Alert>
                </Box>
              )}

              {/* No GPS Data Available - only show if there are assets but no GPS data */}
              {!loadingGPS && !gpsError && gpsData.length === 0 && assets.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                >
                  <Alert 
                    severity="warning"
                    sx={{ fontSize: '12px' }}
                  >
                    No GPS position data available for project assets
                  </Alert>
                </Box>
              )}

              {/* GPS Loading Indicator - only show when loading GPS for assets */}
              {loadingGPS && gpsData.length === 0 && !assetsLoading && assets.length > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: 'white',
                    padding: 1,
                    borderRadius: 1,
                    boxShadow: 2,
                  }}
                >
                  <CircularProgress size={16} />
                  <span style={{ fontSize: '12px' }}>Loading positions...</span>
                </Box>
              )}

              {/* Custom Map Controls */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                {mapControls.map((control, index) => (
                  <IconButton
                    key={index}
                    onClick={() => handleMapControl(control.action)}
                    size="small"
                    sx={{
                      bgcolor: 'white',
                      boxShadow: 2,
                      width: 32,
                      height: 32,
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    {control.icon}
                  </IconButton>
                ))}
              </Box>

            </Box>
            
          </Paper>
        </Grid>
        </Grid>

        {/* Navigation Bar */}
        <Box>
          <NavigationBar />
        </Box>

        {/* Content Area - Renders nested routes */}
        <Box sx={{ 
          flex: 1,
          p: 0,
          overflow: 'auto'
        }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectDetails;
